// Alquiler de material del evento (ficha del presupuesto, formato nuevo).
//
// Sustituye a la tarjeta antigua de alquiler en la pestaña Material:
// - Validación como la del material: "Pendiente de validar" / "Validado por X",
//   con Validar y Reabrir. Validado, las líneas quedan en solo lectura (el
//   backend responde 409 si se intenta cambiar).
// - Buscador del catálogo de alquiler (texto + sección) contra el backend,
//   ordenado por veces alquilado, y visor del catálogo con fotos del
//   proveedor (CatalogoFotosAlquiler) que añade con la misma función.
// - Días de alquiler de toda la orden (1, 2, 3 o un número): Alea cobra por
//   días de uso. Con más días que los del evento hay que confirmar las fechas
//   de la orden antes de enviarla.
// - Tabla de líneas con cantidad editable (los días salen de arriba), modalidad
//   de transporte, transporte estimado, fianza, observaciones y totales (coste,
//   cobro al cliente y margen). Las líneas de venta al cliente (una por
//   artículo) las mantiene el backend (aparecen en Líneas y menús).
// - Cobro al cliente por línea: casilla "Cobrar" (desmarcada, sin línea de
//   venta), chips +25 (defecto) +50 +75 +100 y campo libre "% o €" (número
//   solo = porcentaje; con € = precio por unidad). Nunca por debajo del mínimo
//   (+25 sobre el alquiler; limpieza y porte a coste). Se puede cambiar con el
//   alquiler validado (la validación es del pedido, no del cobro).
// - Recepción en el evento: casilla "Recepción comprobada en el evento" e
//   incidencias (la comprobación se hace en la hoja de material impresa).
// - Orden de pedido al proveedor: preparar, editar entrega y recogida,
//   confirmar fechas, descargar PDF, enviar por email, confirmar con la
//   cotización real y marcar recogida y devolución.
// - Cotización recibida por correo (el PDF del proveedor leído por el
//   backend): resumen, diferencias con la orden y botones para aplicarla como
//   coste real o descartarla.
//
// Todo lo calcula el backend (GET/PUT /presupuestos/{id}/alquiler y
// /orden-alquiler); aquí solo se muestra y se guarda.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { presupuestosAPI, materialesAPI, catalogosAlquilerAPI } from '../../api/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import CatalogoFotosAlquiler, { textoUsoAlquiler } from './CatalogoFotosAlquiler';
import { rangoFechasMultievento } from '../../utils/multieventoCabecera';
import useEsMovil from '../../hooks/useEsMovil';

export const SECCIONES_ALQUILER = [
  'Vajilla', 'Cristalería', 'Cubertería', 'Mantelería', 'Mobiliario', 'Maquinaria', 'Servilletas',
  'Menaje de servicio', 'Miniaturas', 'Menaje de cocina', 'Bandejas', 'Coctelería', 'Isotérmicos', 'Otros',
];

const MAX_RESULTADOS = 50;

const ESTADOS_ORDEN = {
  borrador: { texto: 'Borrador', clase: 'bg-amber-100 text-amber-700' },
  enviada: { texto: 'Enviada', clase: 'bg-green-100 text-green-700' },
  confirmada: { texto: 'Confirmada', clase: 'bg-blue-100 text-blue-700' },
  recogida: { texto: 'Recogida', clase: 'bg-purple-100 text-purple-700' },
  devuelta: { texto: 'Devuelta', clase: 'bg-gray-200 text-gray-700' },
  // El evento se canceló con la orden enviada o confirmada
  anulada: { texto: 'Anulada', clase: 'bg-red-100 text-red-700' },
};

const MODALIDADES = [
  { valor: 'transporte_proveedor', texto: 'Transporte del proveedor al evento' },
  { valor: 'recogida_almacen', texto: 'Recogida en sus instalaciones' },
];

const claseBoton = 'h-11 md:h-[34px] px-3 rounded-lg border text-sm md:text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const botonPrincipal = `${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`;
const botonSecundario = `${claseBoton} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50`;
const claseTh = 'px-2 py-2 text-xs font-bold tracking-wide uppercase text-[#5B6B64] border-b border-[#E1E6E3]';
const claseTd = 'px-2 py-2 text-sm border-b border-[#EEF1EF] align-middle';
const claseInput = 'px-2 py-1.5 min-h-[44px] md:min-h-0 text-base md:text-sm border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary disabled:bg-gray-50 disabled:text-gray-500';
const claseEtiqueta = 'block text-xs font-semibold text-[#5B6B64] mb-1';

const euros = (n) => `${(Number(n) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const precio = (n) => (Number(n) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

const formatFechaHora = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  const f = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`);
  if (Number.isNaN(f.getTime())) return '';
  return f.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatFecha = (valor) => {
  const t = String(valor || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
  return `${t.slice(8, 10)}/${t.slice(5, 7)}/${t.slice(0, 4)}`;
};

const formatDiaMes = (valor) => formatFecha(valor).slice(0, 5);

const DIAS_ALQUILER_MAX = 30;

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const numero = (texto) => {
  const n = parseFloat(String(texto ?? '').replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const mensajeError = (err, porDefecto) => {
  const detalle = err?.response?.data?.detail;
  if (typeof detalle === 'string') return detalle;
  if (Array.isArray(detalle)) return detalle.map(d => d?.msg || '').filter(Boolean).join('; ') || porDefecto;
  return err?.message || porDefecto;
};

// Campos editables de la orden (el resto lo pone el backend)
const formularioOrden = (orden) => ({
  modalidad: orden?.modalidad || 'transporte_proveedor',
  fecha_entrega: String(orden?.fecha_entrega || '').slice(0, 10),
  hora_entrega: orden?.hora_entrega || '',
  fecha_recogida: String(orden?.fecha_recogida || '').slice(0, 10),
  hora_recogida: orden?.hora_recogida || '',
  direccion_entrega: orden?.direccion_entrega || '',
  contacto_nombre: orden?.contacto_en_sitio?.nombre || '',
  contacto_telefono: orden?.contacto_en_sitio?.telefono || '',
  observaciones: orden?.observaciones || '',
});

const cuerpoOrden = (f) => ({
  modalidad: f.modalidad,
  fecha_entrega: f.fecha_entrega || null,
  hora_entrega: f.hora_entrega || null,
  fecha_recogida: f.fecha_recogida || null,
  hora_recogida: f.hora_recogida || null,
  direccion_entrega: f.direccion_entrega || '',
  contacto_en_sitio: { nombre: f.contacto_nombre || '', telefono: f.contacto_telefono || '' },
  observaciones: f.observaciones || '',
});

// Cotización recibida del proveedor por correo, pendiente de revisar
const TarjetaCotizacionRecibida = ({ cotizacion, proveedor, ocupado, onAplicar, onDescartar }) => {
  const propuesta = cotizacion.propuesta_coste_real || null;
  const comparacion = cotizacion.diferencias || null;
  const textos = Array.isArray(comparacion?.diferencias) ? comparacion.diferencias : [];
  const fecha = formatDiaMes(cotizacion.email_fecha || cotizacion.fecha);
  const documento = cotizacion.documento || null;
  const tipoDoc = { albaran: 'albarán', factura: 'factura', presupuesto: 'presupuesto' }[documento?.tipo] || 'documento';
  return (
    <div className="rounded-[12px] border-2 border-[#F4D9AE] bg-[#FDF6EA] px-3.5 py-3 flex flex-col gap-2" data-testid="alquiler-cotizacion-recibida">
      <p className="text-sm font-bold text-[#7A3A06]">
        Cotización recibida de {proveedor}{fecha ? ` el ${fecha}` : ''}
        {propuesta && (
          <span className="font-semibold">
            {`: material ${euros(propuesta.material)}, transporte ${euros(propuesta.transporte)}, lavado ${euros(propuesta.lavado)}`}
            {Number(propuesta.faltas) > 0 ? `, faltas ${euros(propuesta.faltas)}` : ''}
            {`, total ${euros(propuesta.total)} + IVA`}
          </span>
        )}
      </p>
      {documento?.numero && (
        <p className="text-xs text-[#5B6B64]">
          {`${tipoDoc.charAt(0).toUpperCase()}${tipoDoc.slice(1)} ${documento.numero}`}
          {documento.fecha_entrega ? ` · entrega ${formatFecha(documento.fecha_entrega)}` : ''}
          {documento.fecha_devolucion ? ` · devolución ${formatFecha(documento.fecha_devolucion)}` : ''}
          {documento.dias ? ` · ${documento.dias === 1 ? '1 día' : `${documento.dias} días`} de alquiler` : ''}
          {documento.coherente === false ? ' · los totales del PDF no cuadran, revísalo' : ''}
        </p>
      )}
      {cotizacion.sin_pdf && (
        <p className="text-xs text-[#7A3A06]">El correo no trae PDF: ábrelo y confirma el coste a mano.</p>
      )}
      {cotizacion.error && <p className="text-xs text-[#8A1C12]">{cotizacion.error}</p>}
      {propuesta && (textos.length > 0 ? (
        <ul className="list-disc pl-5 text-xs text-[#8A1C12] flex flex-col gap-0.5" data-testid="alquiler-cotizacion-diferencias">
          {textos.map(t => <li key={t}>{t}</li>)}
        </ul>
      ) : (
        <p className="text-xs text-[#0F5E43]">Cuadra con la orden: mismos artículos, unidades y precios.</p>
      ))}
      <div className="flex flex-wrap gap-2">
        {propuesta && (
          <button type="button" onClick={onAplicar} disabled={ocupado} className={botonPrincipal} data-testid="alquiler-cotizacion-aplicar">
            Aplicar como coste real
          </button>
        )}
        <button type="button" onClick={onDescartar} disabled={ocupado} className={botonSecundario} data-testid="alquiler-cotizacion-descartar">
          Descartar
        </button>
        {cotizacion.email_id && (
          <a
            href={`/emails?email=${encodeURIComponent(cotizacion.email_id)}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`${botonSecundario} inline-flex items-center`}
            data-testid="alquiler-cotizacion-ver-correo"
          >
            Ver correo
          </a>
        )}
      </div>
    </div>
  );
};

const MARGENES = [25, 50, 75, 100];
const MARGEN_MINIMO = 25;

// "30" o "30 %" -> { margen: 30 }; "12€", "12 €", "12e" -> { precio: 12 }; null si no vale
export const interpretarCobro = (texto) => {
  const t = String(texto ?? '').trim().replace(/\s+/g, ' ');
  const m = /^(\d+(?:[.,]\d+)?)\s*(%|€|e|eur|euros?)?$/i.exec(t);
  if (!m) return null;
  const n = parseFloat(m[1].replace(',', '.'));
  if (Number.isNaN(n)) return null;
  const simbolo = (m[2] || '').toLowerCase();
  if (simbolo && simbolo !== '%') return { precio: n };
  return { margen: n };
};

// Cobro al cliente de una línea: casilla, chips de margen y campo libre
const CobroLinea = ({ linea, onCambiar, ocupado }) => {
  const [texto, setTexto] = useState('');
  const [mensaje, setMensaje] = useState(null);
  const cobrar = linea.cobrar_cliente !== false;
  const manual = linea.precio_unitario_manual !== null && linea.precio_unitario_manual !== undefined;
  const aMano = !!linea.precio_a_mano;
  const margenActivo = cobrar && !aMano ? Number(linea.margen_pct) || MARGEN_MINIMO : null;
  const minimo = Number(linea.precio_minimo) || 0;

  const aplicarTexto = () => {
    const t = texto.trim();
    if (!t) return;
    const r = interpretarCobro(t);
    if (!r) {
      setMensaje('Escribe un porcentaje (60 %) o un importe (70 €).');
      return;
    }
    setMensaje(null);
    setTexto('');
    if (r.margen !== undefined) {
      if (r.margen < MARGEN_MINIMO) {
        setMensaje(`Mínimo 25 %: ${precio(minimo)} €/ud`);
        onCambiar({ margen_pct: MARGEN_MINIMO });
        return;
      }
      onCambiar({ margen_pct: r.margen });
      return;
    }
    if (r.precio < minimo - 0.005) {
      setMensaje(`Mínimo 25 %: ${precio(minimo)} €/ud`);
      onCambiar({ margen_pct: MARGEN_MINIMO });
      return;
    }
    onCambiar({ precio_unitario_manual: r.precio });
  };

  const margenTexto = (n) => (n === null || n === undefined ? '' : `${Number(n).toLocaleString('es-ES', { maximumFractionDigits: 1 })} %`);

  return (
    <div className="flex flex-col gap-1 md:min-w-[260px]" data-testid={`alquiler-cobro-${linea.material_id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <label className="flex items-center gap-1 min-h-[40px] md:min-h-0 text-sm md:text-xs font-semibold text-[#16221D] mr-1">
          <input
            type="checkbox"
            checked={cobrar}
            onChange={(e) => onCambiar({ cobrar_cliente: e.target.checked })}
            disabled={ocupado}
            className="h-5 w-5 md:h-4 md:w-4 accent-[#1A9169]"
            data-testid={`alquiler-cobrar-${linea.material_id}`}
          />
          Cobrar
        </label>
        {MARGENES.map((m) => {
          const activo = margenActivo === m;
          return (
            <button
              key={m}
              type="button"
              onClick={() => { setMensaje(null); if (!activo || manual) onCambiar({ margen_pct: m }); }}
              disabled={!cobrar || ocupado}
              aria-pressed={activo}
              className={`h-10 md:h-[26px] min-w-[44px] md:min-w-0 px-2 md:px-1.5 rounded-md border text-sm md:text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${activo ? 'border-duetto-primary bg-[#E3F3EC] text-[#0F5E43]' : 'border-[#E1E6E3] bg-white text-[#16221D] hover:border-[#9FC7B6]'}`}
              data-testid={`alquiler-margen-${linea.material_id}-${m}`}
            >
              +{m}
            </button>
          );
        })}
        <input
          type="text"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={aplicarTexto}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          disabled={!cobrar || ocupado}
          placeholder="% o €"
          className={`${claseInput} w-20 text-right`}
          aria-label={`Margen o precio por unidad de ${linea.nombre}`}
          data-testid={`alquiler-cobro-libre-${linea.material_id}`}
        />
      </div>
      {cobrar ? (
        <p className="text-xs text-[#5B6B64] tabular-nums" data-testid={`alquiler-cobro-texto-${linea.material_id}`}>
          {euros(linea.importe_venta)} · {precio(linea.precio_venta)} €/ud
          {linea.margen_resultante_pct !== null && linea.margen_resultante_pct !== undefined
            ? ` · margen ${margenTexto(linea.margen_resultante_pct)}` : ''}
          {aMano && <span className="ml-1.5 px-1.5 py-px rounded bg-[#FDF0DC] text-[#7A3A06] text-xs md:text-[10px] font-semibold">precio a mano</span>}
        </p>
      ) : (
        <p className="text-xs text-[#5B6B64]">No se cobra</p>
      )}
      {(mensaje || (cobrar && linea.aviso_minimo)) && (
        <p className="text-xs text-[#7A3A06]">{mensaje || linea.aviso_minimo}</p>
      )}
    </div>
  );
};

// Recepción en el evento (registro ligero; la comprobación se hace en la hoja impresa)
const RecepcionAlquiler = ({ recepcion, ocupado, onGuardar }) => {
  const [incidencias, setIncidencias] = useState(recepcion?.incidencias || '');
  const guardadas = recepcion?.incidencias || '';
  useEffect(() => { setIncidencias(guardadas); }, [guardadas]);
  const comprobada = !!recepcion?.comprobada;
  return (
    <div className="rounded-[12px] border border-[#E1E6E3] p-3.5 flex flex-col gap-2" data-testid="alquiler-recepcion">
      <h4 className="text-sm font-bold text-[#16221D]">Recepción en el evento</h4>
      <p className="text-xs text-[#5B6B64]">Se comprueba en la hoja de material impresa, en el lugar del evento.</p>
      <label className="flex items-center gap-2 text-sm font-semibold text-[#16221D]">
        <input
          type="checkbox"
          checked={comprobada}
          onChange={(e) => onGuardar({ comprobada: e.target.checked })}
          disabled={ocupado}
          className="h-4 w-4 accent-[#1A9169]"
          data-testid="alquiler-recepcion-comprobada"
        />
        Recepción comprobada en el evento
      </label>
      {comprobada && (
        <p className="text-xs text-[#0F5E43]" data-testid="alquiler-recepcion-quien">
          Comprobada por {recepcion.por || 'alguien'}{recepcion.en ? ` el ${formatFechaHora(recepcion.en)}` : ''}
        </p>
      )}
      <div>
        <label className={claseEtiqueta} htmlFor="alquiler-recepcion-incidencias">Incidencias</label>
        <textarea
          id="alquiler-recepcion-incidencias"
          rows={2}
          value={incidencias}
          onChange={(e) => setIncidencias(e.target.value)}
          onBlur={() => { if (incidencias.trim() !== guardadas.trim()) onGuardar({ comprobada, incidencias }); }}
          disabled={ocupado}
          placeholder="Faltas, roturas o cambios respecto a lo pedido"
          className={`${claseInput} w-full`}
          data-testid="alquiler-recepcion-incidencias"
        />
        {recepcion?.incidencias && recepcion?.incidencias_por && (
          <p className="text-xs text-[#5B6B64] mt-1">
            Anotadas por {recepcion.incidencias_por}{recepcion.incidencias_en ? ` el ${formatFechaHora(recepcion.incidencias_en)}` : ''}
          </p>
        )}
      </div>
    </div>
  );
};

const AlquilerEvento = ({ presupuestoId, presupuesto, jornadaId = null, datosIniciales = null, onCambio, onEstadoGuardado }) => {
  // Móvil (< 768 px): las líneas y la orden van en tarjetas en vez de en tabla
  const esMovil = useEsMovil();
  const [datos, setDatos] = useState(datosIniciales);
  const [cargando, setCargando] = useState(!datosIniciales);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null); // { tipo: 'ok' | 'error' | 'info', texto }
  const [ocupado, setOcupado] = useState(false);

  // Borradores de cantidad y días por línea: { material_id: { cantidad, dias } }
  const [borradores, setBorradores] = useState({});
  const borradoresRef = useRef(borradores);
  borradoresRef.current = borradores;
  const datosRef = useRef(datos);
  datosRef.current = datos;
  const temporizadorLineasRef = useRef(null);
  const colaRef = useRef(Promise.resolve());

  // Transporte, fianza y observaciones (se guardan al salir del campo)
  const [transporte, setTransporte] = useState('');
  const [fianza, setFianza] = useState('');
  const [observaciones, setObservaciones] = useState('');
  // Días de alquiler de toda la orden (campo numérico)
  const [diasTexto, setDiasTexto] = useState('');

  // Buscador del catálogo
  const [busqueda, setBusqueda] = useState('');
  const [seccion, setSeccion] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [errorBusqueda, setErrorBusqueda] = useState(null);
  const [cantidadesNuevas, setCantidadesNuevas] = useState({}); // { material_id: texto }

  // Veces que se ha alquilado cada material (null: sin datos, no se muestra)
  const [usoAlquiler, setUsoAlquiler] = useState(null);
  // Catálogos con fotos de los proveedores (visor)
  const [catalogosFotos, setCatalogosFotos] = useState([]);
  const [catalogoAbierto, setCatalogoAbierto] = useState(false);

  // Orden al proveedor
  const [formOrden, setFormOrden] = useState(formularioOrden(null));
  const [ordenModificada, setOrdenModificada] = useState(false);
  const [modalEnvio, setModalEnvio] = useState(null); // { para, cc, asunto, cuerpo }
  const [modalConfirmar, setModalConfirmar] = useState(null); // { material, transporte, lavado, faltas, numero, fianza }
  const [modalDevuelta, setModalDevuelta] = useState(null); // { fecha }

  const onCambioRef = useRef(onCambio);
  onCambioRef.current = onCambio;
  const onEstadoGuardadoRef = useRef(onEstadoGuardado);
  onEstadoGuardadoRef.current = onEstadoGuardado;

  const aplicar = useCallback((data, { avisar = true } = {}) => {
    if (!data || !Array.isArray(data.lineas)) return;
    setDatos(data);
    setTransporte(data.alquiler_transporte ? String(data.alquiler_transporte) : '');
    setFianza(data.alquiler_fianza ? String(data.alquiler_fianza) : '');
    setObservaciones(data.observaciones_alquiler || '');
    setDiasTexto(data.dias_alquiler ? String(data.dias_alquiler) : '');
    if (data.aviso) setAviso({ tipo: 'info', texto: data.aviso });
    if (avisar && onCambioRef.current) onCambioRef.current(data);
  }, []);

  const cargar = useCallback(async ({ avisar = false } = {}) => {
    if (!presupuestoId) return;
    try {
      const { data } = await presupuestosAPI.getAlquiler(presupuestoId, jornadaId);
      if (!data || !Array.isArray(data.lineas)) {
        setError('No se pudo cargar el alquiler del evento.');
        return;
      }
      setError(null);
      aplicar(data, { avisar });
    } catch (err) {
      setError(mensajeError(err, 'No se pudo cargar el alquiler del evento.'));
    } finally {
      setCargando(false);
    }
  }, [presupuestoId, jornadaId, aplicar]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Uso del material de alquiler y catálogos con fotos (una vez; si fallan, no se muestran)
  useEffect(() => {
    let cancelado = false;
    const pedirUso = materialesAPI.usoAlquiler ? materialesAPI.usoAlquiler() : api.get('/materiales/uso-alquiler');
    pedirUso
      .then(({ data }) => { if (!cancelado && data && typeof data === 'object' && !Array.isArray(data)) setUsoAlquiler(data); })
      .catch(() => {});
    catalogosAlquilerAPI.listar()
      .then(({ data }) => { if (!cancelado && Array.isArray(data)) setCatalogosFotos(data); })
      .catch(() => {});
    return () => { cancelado = true; };
  }, []);

  // Al salir con cantidades a medio guardar, se guardan ya
  const guardarRef = useRef(null);
  useEffect(() => () => {
    if (!temporizadorLineasRef.current) return;
    clearTimeout(temporizadorLineasRef.current);
    temporizadorLineasRef.current = null;
    if (guardarRef.current) guardarRef.current();
  }, []);

  // El formulario de la orden se rellena con lo guardado mientras no se edite
  const orden = datos?.orden || null;
  const firmaOrden = orden ? JSON.stringify(formularioOrden(orden)) : '';
  useEffect(() => {
    if (ordenModificada) return;
    setFormOrden(firmaOrden ? JSON.parse(firmaOrden) : formularioOrden(null));
  }, [firmaOrden, ordenModificada]);

  // ----- Guardado de líneas y datos del alquiler -----

  // Líneas tal como se ven (lo guardado con los borradores encima)
  // `cobro`: { material_id, ...campos } con el cobro al cliente de UNA línea
  // (los campos de cobro que no se envían se conservan en el backend)
  const lineasParaEnviar = (extra = null, quitar = null, cobro = null) => {
    const actual = datosRef.current;
    const bor = borradoresRef.current;
    const lineas = (actual?.lineas || [])
      .filter(l => l.material_id !== quitar)
      .map((l) => {
        const b = bor[l.material_id] || {};
        const cantidad = b.cantidad !== undefined ? numero(b.cantidad) : null;
        const { material_id: idCobro, ...camposCobro } = cobro || {};
        return {
          material_id: l.material_id,
          cantidad: cantidad !== null && cantidad >= 0 ? cantidad : (Number(l.cantidad) || 0),
          // Los días son los de toda la orden (los aplica el backend)
          dias: Number(l.dias) || actual?.dias_alquiler || actual?.dias_por_defecto || 1,
          ...(cobro && idCobro === l.material_id ? camposCobro : {}),
        };
      });
    if (extra) lineas.push(extra);
    return lineas;
  };

  // Todas las escrituras van en cola (nunca dos PUT a la vez ni fuera de orden)
  const enCola = (tarea) => {
    const siguiente = colaRef.current.then(tarea, tarea);
    colaRef.current = siguiente.catch(() => {});
    return siguiente;
  };

  const guardar = (cuerpoExtra = {}, { textoOk = null, extra = null, quitar = null, cobro = null } = {}) => enCola(async () => {
    const enviadoBorradores = borradoresRef.current;
    const cuerpo = { lineas: lineasParaEnviar(extra, quitar, cobro), ...cuerpoExtra };
    setAviso(null);
    if (onEstadoGuardadoRef.current) onEstadoGuardadoRef.current('inicio');
    try {
      const { data } = await presupuestosAPI.setAlquiler(presupuestoId, cuerpo, jornadaId);
      // Se quitan los borradores enviados que no se han vuelto a tocar
      setBorradores((prev) => {
        const r = { ...prev };
        Object.keys(enviadoBorradores).forEach((k) => {
          if (JSON.stringify(prev[k]) === JSON.stringify(enviadoBorradores[k])) delete r[k];
        });
        return r;
      });
      aplicar(data);
      if (textoOk && !data?.aviso) setAviso({ tipo: 'ok', texto: textoOk });
      if (onEstadoGuardadoRef.current) onEstadoGuardadoRef.current('ok');
      return data;
    } catch (err) {
      if (onEstadoGuardadoRef.current) onEstadoGuardadoRef.current('error');
      if (err?.response?.status === 409) {
        setBorradores({});
        setAviso({ tipo: 'error', texto: 'El alquiler está validado. Reabre la validación para cambiarlo.' });
        cargar();
      } else {
        setAviso({ tipo: 'error', texto: `No se pudo guardar: ${mensajeError(err, 'error desconocido')}` });
      }
      return undefined;
    }
  });

  guardarRef.current = guardar;

  const programarGuardadoLineas = () => {
    if (temporizadorLineasRef.current) clearTimeout(temporizadorLineasRef.current);
    temporizadorLineasRef.current = setTimeout(() => {
      temporizadorLineasRef.current = null;
      guardar();
    }, 600);
  };

  const guardarLineasYa = () => {
    if (!temporizadorLineasRef.current) return;
    clearTimeout(temporizadorLineasRef.current);
    temporizadorLineasRef.current = null;
    guardar();
  };

  const cambiarBorrador = (materialId, campo, valor) => {
    setBorradores(prev => ({ ...prev, [materialId]: { ...(prev[materialId] || {}), [campo]: valor } }));
    borradoresRef.current = {
      ...borradoresRef.current,
      [materialId]: { ...(borradoresRef.current[materialId] || {}), [campo]: valor },
    };
    const n = numero(valor);
    if (n === null || n < 0) return;
    programarGuardadoLineas();
  };

  // Días de alquiler de toda la orden: se aplican a todas las líneas
  const cambiarDiasAlquiler = (valor) => {
    const n = numero(valor);
    if (n === null || !Number.isInteger(n) || n < 1 || n > DIAS_ALQUILER_MAX) {
      setAviso({ tipo: 'error', texto: `Los días de alquiler tienen que ser un número entero entre 1 y ${DIAS_ALQUILER_MAX}.` });
      setDiasTexto(datosRef.current?.dias_alquiler ? String(datosRef.current.dias_alquiler) : '');
      return;
    }
    if (n === Number(datosRef.current?.dias_alquiler)) return;
    if (temporizadorLineasRef.current) {
      clearTimeout(temporizadorLineasRef.current);
      temporizadorLineasRef.current = null;
    }
    guardar({ dias_alquiler: n }, { textoOk: `Días de alquiler: ${n}.` });
  };

  // Cobro al cliente de una línea (también con el alquiler validado)
  const cambiarCobro = (materialId, campos) => {
    if (temporizadorLineasRef.current) {
      clearTimeout(temporizadorLineasRef.current);
      temporizadorLineasRef.current = null;
    }
    guardar({}, { cobro: { material_id: materialId, ...campos } });
  };

  // Recepción en el evento
  const guardarRecepcion = async (cuerpo) => {
    setOcupado(true);
    setAviso(null);
    if (onEstadoGuardadoRef.current) onEstadoGuardadoRef.current('inicio');
    try {
      await colaRef.current;
      const { data } = await presupuestosAPI.recepcionAlquiler(presupuestoId, cuerpo, jornadaId);
      aplicar(data);
      if (onEstadoGuardadoRef.current) onEstadoGuardadoRef.current('ok');
    } catch (err) {
      setAviso({ tipo: 'error', texto: mensajeError(err, 'No se pudo guardar la recepción.') });
      if (onEstadoGuardadoRef.current) onEstadoGuardadoRef.current('error');
    } finally {
      setOcupado(false);
    }
  };

  const quitarLinea = (linea) => {
    if (!window.confirm(`¿Quitar "${linea.nombre}" del alquiler?`)) return;
    if (temporizadorLineasRef.current) {
      clearTimeout(temporizadorLineasRef.current);
      temporizadorLineasRef.current = null;
    }
    guardar({}, { quitar: linea.material_id, textoOk: 'Artículo quitado.' });
  };

  // Añade una línea (o suma cantidad si el material ya está: el backend suma
  // las líneas repetidas). La usan el buscador y el visor del catálogo.
  const anadirLinea = (materialId, nombre, n) => {
    if (n === null || n <= 0) {
      setAviso({ tipo: 'error', texto: 'Escribe una cantidad mayor que 0 para añadir el artículo.' });
      return Promise.resolve(undefined);
    }
    if (temporizadorLineasRef.current) {
      clearTimeout(temporizadorLineasRef.current);
      temporizadorLineasRef.current = null;
    }
    const yaEsta = (datosRef.current?.lineas || []).some(l => l.material_id === materialId);
    return guardar({}, {
      extra: { material_id: materialId, cantidad: n },
      textoOk: yaEsta ? `Sumado: ${n} más de ${nombre}.` : `Añadido: ${nombre}.`,
    });
  };

  const anadir = (mat) => {
    anadirLinea(mat.id, mat.nombre, numero(cantidadesNuevas[mat.id] ?? '1'))
      .then((data) => {
        if (data) setCantidadesNuevas(prev => { const r = { ...prev }; delete r[mat.id]; return r; });
      });
  };

  const guardarImporte = (campo, texto, actual) => {
    const n = texto === '' ? 0 : numero(texto);
    if (n === null || n < 0) {
      setAviso({ tipo: 'error', texto: 'Escribe un importe válido (0 o más).' });
      return;
    }
    if (Math.abs(n - (Number(actual) || 0)) < 0.001) return;
    guardar({ [campo]: n });
  };

  // ----- Validación -----

  const validarOReabrir = async (accion) => {
    guardarLineasYa();
    setOcupado(true);
    setAviso(null);
    if (onEstadoGuardado) onEstadoGuardado('inicio');
    try {
      await colaRef.current;
      const { data } = accion === 'validar'
        ? await presupuestosAPI.validarAlquiler(presupuestoId, jornadaId)
        : await presupuestosAPI.reabrirAlquiler(presupuestoId, jornadaId);
      setBorradores({});
      aplicar(data);
      setAviso({ tipo: 'ok', texto: accion === 'validar' ? 'Alquiler validado.' : 'Alquiler reabierto.' });
      if (onEstadoGuardado) onEstadoGuardado('ok');
    } catch (err) {
      setAviso({ tipo: 'error', texto: mensajeError(err, 'No se pudo cambiar la validación.') });
      if (onEstadoGuardado) onEstadoGuardado('error');
    } finally {
      setOcupado(false);
    }
  };

  // ----- Buscador del catálogo -----

  useEffect(() => {
    const texto = busqueda.trim();
    if (!texto && !seccion) {
      setResultados([]);
      setErrorBusqueda(null);
      setBuscando(false);
      return undefined;
    }
    let cancelado = false;
    setBuscando(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await materialesAPI.buscarAlquiler({ q: texto, seccion });
        if (cancelado) return;
        const lista = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        setResultados(lista.filter(m => m && m.activo !== false));
        setErrorBusqueda(null);
      } catch (err) {
        if (!cancelado) {
          setResultados([]);
          setErrorBusqueda(mensajeError(err, 'No se pudo buscar en el catálogo.'));
        }
      } finally {
        if (!cancelado) setBuscando(false);
      }
    }, 300);
    return () => { cancelado = true; clearTimeout(t); };
  }, [busqueda, seccion]);

  // ----- Orden al proveedor -----

  const accionOrden = async (fn, textoOk) => {
    guardarLineasYa();
    setOcupado(true);
    setAviso(null);
    if (onEstadoGuardado) onEstadoGuardado('inicio');
    try {
      await colaRef.current;
      const resultado = await fn();
      setOrdenModificada(false);
      await cargar({ avisar: true });
      if (textoOk) setAviso({ tipo: 'ok', texto: textoOk });
      if (onEstadoGuardado) onEstadoGuardado('ok');
      return resultado || true;
    } catch (err) {
      setAviso({ tipo: 'error', texto: mensajeError(err, 'No se pudo completar la acción.') });
      if (onEstadoGuardado) onEstadoGuardado('error');
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const prepararOrden = () => accionOrden(
    () => presupuestosAPI.setOrdenAlquiler(presupuestoId, {}, jornadaId),
    'Orden preparada: revisa la entrega y la recogida y guárdala.',
  );

  const guardarOrden = () => accionOrden(
    () => presupuestosAPI.setOrdenAlquiler(presupuestoId, cuerpoOrden(formOrden), jornadaId),
    'Orden guardada.',
  );

  const cambiarOrden = (campo, valor) => {
    setFormOrden(prev => ({ ...prev, [campo]: valor }));
    setOrdenModificada(true);
  };

  // Confirma las fechas de la orden (con los cambios del formulario, si los hay)
  const confirmarFechas = () => accionOrden(
    () => presupuestosAPI.setOrdenAlquiler(presupuestoId, {
      ...(ordenModificada ? cuerpoOrden(formOrden) : {}),
      fechas_confirmadas: true,
    }, jornadaId),
    'Fechas de la orden confirmadas.',
  );

  // ----- Cotización recibida por correo -----

  const aplicarCotizacionRecibida = () => {
    if (!window.confirm('¿Aplicar la cotización del proveedor como coste real del alquiler?')) return;
    accionOrden(
      () => presupuestosAPI.aplicarCotizacionAlquiler(presupuestoId, jornadaId),
      'Cotización aplicada: el coste real pasa a la rentabilidad.',
    );
  };

  const descartarCotizacionRecibida = () => {
    if (!window.confirm('¿Descartar la cotización recibida? No cambia el coste del alquiler.')) return;
    accionOrden(
      () => presupuestosAPI.descartarCotizacionAlquiler(presupuestoId, jornadaId),
      'Cotización descartada.',
    );
  };

  const descargarPdf = async () => {
    setOcupado(true);
    setAviso(null);
    try {
      if (ordenModificada) await presupuestosAPI.setOrdenAlquiler(presupuestoId, cuerpoOrden(formOrden), jornadaId);
      const respuesta = await presupuestosAPI.descargarOrdenAlquilerPdf(presupuestoId, jornadaId);
      const url = window.URL.createObjectURL(new Blob([respuesta.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `orden_alquiler_${presupuesto?.numero || String(presupuestoId).slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      if (ordenModificada) {
        setOrdenModificada(false);
        cargar();
      }
    } catch (err) {
      setAviso({ tipo: 'error', texto: 'No se pudo descargar el PDF de la orden.' });
    } finally {
      setOcupado(false);
    }
  };

  const abrirEnvio = () => {
    const proveedorNombre = orden?.proveedor_nombre || datos?.proveedor?.nombre || '';
    // Fecha del evento en el texto: la de la jornada de la orden; sin jornada,
    // en un multievento de varios días, el rango ("17 al 18/11/2026")
    const jornadaOrden = jornadaId ? (presupuesto?.jornadas || []).find(j => j?.id === jornadaId) : null;
    const fechaEvento = jornadaOrden?.fecha
      ? formatFecha(jornadaOrden.fecha)
      : (rangoFechasMultievento(presupuesto) || formatFecha(presupuesto?.fecha_evento));
    const ref = presupuesto?.numero ? `presupuesto ${presupuesto.numero}` : 'nuestro evento';
    setModalEnvio({
      para: orden?.proveedor_email || datos?.proveedor?.email || '',
      cc: '',
      // El número con # en el asunto permite reconocer la respuesta del proveedor
      asunto: `Pedido de alquiler de material${presupuesto?.numero ? ` #${presupuesto.numero}` : ''}${fechaEvento ? ` · evento del ${fechaEvento}` : ''}`,
      cuerpo: [
        `Hola${proveedorNombre ? `, ${proveedorNombre}` : ''}:`,
        '',
        `Os adjuntamos la orden de pedido de material de alquiler para ${ref}${fechaEvento ? ` del ${fechaEvento}` : ''}.`,
        'En el PDF van los artículos, las cantidades y los datos de entrega y recogida.',
        '',
        'Por favor, confirmadnos disponibilidad y enviadnos vuestro presupuesto.',
        '',
        'Un saludo,',
        'Duetto Catering',
      ].join('\n'),
    });
  };

  const enviarOrden = async () => {
    if (!modalEnvio) return;
    if (!String(modalEnvio.para || '').trim()) {
      setAviso({ tipo: 'error', texto: 'Falta el email del proveedor.' });
      return;
    }
    const datosEnvio = { ...modalEnvio };
    const ok = await accionOrden(async () => {
      if (ordenModificada) await presupuestosAPI.setOrdenAlquiler(presupuestoId, cuerpoOrden(formOrden), jornadaId);
      return presupuestosAPI.enviarOrdenAlquiler(presupuestoId, {
        para: datosEnvio.para.trim(),
        cc: datosEnvio.cc.split(',').map(c => c.trim()).filter(Boolean),
        asunto: datosEnvio.asunto,
        cuerpo: datosEnvio.cuerpo,
      }, jornadaId);
    }, `Orden enviada a ${datosEnvio.para.trim()}.`);
    if (ok) setModalEnvio(null);
  };

  const abrirConfirmar = () => {
    const cr = orden?.coste_real || null;
    const tot = datos?.totales || {};
    const texto = (v) => (v !== undefined && v !== null && v !== '' ? String(v) : '');
    setModalConfirmar({
      material: texto(cr?.material ?? tot.material),
      transporte: texto(cr?.transporte ?? tot.transporte),
      lavado: texto(cr?.lavado ?? tot.limpieza),
      faltas: texto(cr?.faltas ?? 0),
      numero: cr?.numero_presupuesto_proveedor || '',
      fianza: texto(orden?.fianza_importe ?? datos?.alquiler_fianza ?? 0),
    });
  };

  const totalConfirmar = modalConfirmar
    ? ['material', 'transporte', 'lavado', 'faltas'].reduce((s, k) => s + (numero(modalConfirmar[k]) || 0), 0)
    : 0;

  const confirmarCotizacion = async () => {
    if (!modalConfirmar) return;
    const valores = {};
    for (const k of ['material', 'transporte', 'lavado', 'faltas']) {
      const n = modalConfirmar[k] === '' ? 0 : numero(modalConfirmar[k]);
      if (n === null || n < 0) {
        setAviso({ tipo: 'error', texto: 'Revisa los importes de la cotización (0 o más).' });
        return;
      }
      valores[k] = n;
    }
    const fianzaN = modalConfirmar.fianza === '' ? 0 : numero(modalConfirmar.fianza);
    const ok = await accionOrden(() => presupuestosAPI.confirmarOrdenAlquiler(presupuestoId, {
      coste_real: { ...valores, numero_presupuesto_proveedor: modalConfirmar.numero || '' },
      fianza_importe: fianzaN !== null && fianzaN >= 0 ? fianzaN : 0,
    }, jornadaId), 'Cotización confirmada: el coste real pasa a la rentabilidad.');
    if (ok) setModalConfirmar(null);
  };

  const marcarRecogida = () => {
    if (!window.confirm('¿Marcar el material como recogido?')) return;
    accionOrden(() => presupuestosAPI.estadoOrdenAlquiler(presupuestoId, { estado: 'recogida' }, jornadaId), 'Material marcado como recogido.');
  };

  const marcarDevuelta = async () => {
    if (!modalDevuelta) return;
    const ok = await accionOrden(() => presupuestosAPI.estadoOrdenAlquiler(presupuestoId, {
      estado: 'devuelta',
      fianza_devuelta_en: modalDevuelta.fecha || null,
    }, jornadaId), 'Material marcado como devuelto.');
    if (ok) setModalDevuelta(null);
  };

  const marcarPagado = () => {
    if (!window.confirm('¿Marcar el alquiler (y la fianza) como pagados hoy?')) return;
    accionOrden(() => presupuestosAPI.setOrdenAlquiler(presupuestoId, { fianza_pagada_en: hoyISO() }, jornadaId), 'Pago del alquiler anotado.');
  };

  // ----- Render -----

  if (cargando && !datos) return <p className="text-sm text-[#5B6B64]">Cargando el alquiler del evento...</p>;
  if (!datos) return <p className="text-sm text-[#8A1C12]" data-testid="alquiler-error">{error}</p>;

  const lineas = datos.lineas || [];
  const validado = datos.validado || null;
  const cambiado = !!(validado && datos.huella_cambiada);
  const bloqueado = !!validado;
  const totales = datos.totales || {};
  const modalidad = datos.modalidad_transporte || 'transporte_proveedor';
  const idsEnLista = new Set(lineas.map(l => l.material_id));
  const cantidadesEnLista = {};
  lineas.forEach((l) => { cantidadesEnLista[l.material_id] = Number(l.cantidad) || 0; });
  // Lo más alquilado primero; a igualdad, por nombre
  const vecesAlquilado = (id) => Number(usoAlquiler?.[id]?.eventos) || 0;
  const ordenados = usoAlquiler
    ? [...resultados].sort((a, b) => (vecesAlquilado(b.id) - vecesAlquilado(a.id))
      || String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'))
    : resultados;
  const visibles = ordenados.slice(0, MAX_RESULTADOS);
  const estadoOrden = orden?.estado || 'borrador';
  const infoEstado = ESTADOS_ORDEN[estadoOrden] || ESTADOS_ORDEN.borrador;
  const proveedor = datos.proveedor || null;
  const proveedorOrden = orden ? {
    nombre: orden.proveedor_nombre || proveedor?.nombre,
    email: orden.proveedor_email || proveedor?.email,
    telefono: orden.proveedor_telefono || proveedor?.telefono,
    numero_cliente: orden.numero_cliente_proveedor || proveedor?.numero_cliente,
  } : null;
  const envios = Array.isArray(orden?.envios) ? orden.envios : [];
  const costeReal = orden?.coste_real || null;
  const estimacion = Number(totales.coste_total) || 0;
  const diasAlquiler = Number(datos.dias_alquiler) || Number(datos.dias_por_defecto) || 1;
  const avisoFechas = datos.aviso_fechas || null;
  const cotRecibida = orden?.cotizacion_recibida?.estado === 'pendiente' ? orden.cotizacion_recibida : null;

  let tonoCabecera = 'bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]';
  if (validado && !cambiado) tonoCabecera = 'bg-[#E3F3EC] border-[#BFE3D2] text-[#0F5E43]';

  const valorLinea = (l, campo) => {
    const b = borradores[l.material_id];
    if (b && b[campo] !== undefined) return b[campo];
    return l[campo];
  };

  return (
    <div className="flex flex-col gap-4" data-testid="alquiler-evento">
      <div>
        <h3 className="text-xs font-bold tracking-wider uppercase text-[#5B6B64]">Material de alquiler</h3>
        <p className="text-xs text-[#5B6B64]">Material alquilado a proveedores externos para este evento. Lo que se añade no se repercute al cliente hasta marcar Cobrar en su línea.</p>
      </div>

      {/* Validación */}
      <div
        className={`flex flex-wrap items-center justify-between gap-3 rounded-[12px] border px-4 py-3.5 ${tonoCabecera}`}
        data-testid="alquiler-cabecera-validacion"
        data-estado={validado ? (cambiado ? 'cambiado' : 'validado') : 'pendiente'}
      >
        <div className="min-w-0 flex flex-col gap-0.5">
          {!validado && (
            <>
              <span className="text-base font-extrabold">Pendiente de validar</span>
              <span className="text-xs">Revisa los artículos, las cantidades y los días y valídalos antes de pedir al proveedor.</span>
            </>
          )}
          {validado && !cambiado && (
            <span className="flex flex-col gap-0.5" data-testid="alquiler-validado">
              <span className="text-base font-extrabold">
                Validado por {validado.por || 'alguien'}{validado.en ? ` el ${formatFechaHora(validado.en)}` : ''}
              </span>
              <span className="text-xs">Para cambiar artículos o cantidades, reabre la validación.</span>
            </span>
          )}
          {cambiado && (
            <span className="flex flex-col gap-0.5" data-testid="alquiler-huella-cambiada">
              <span className="text-base font-extrabold">Ha cambiado desde la validación</span>
              <span className="text-xs">
                Cambió el evento después de validar{validado.por ? ` (validado por ${validado.por})` : ''}: reábrelo, revisa las cantidades y vuelve a validar.
              </span>
            </span>
          )}
        </div>
        {validado ? (
          <button type="button" onClick={() => validarOReabrir('reabrir')} disabled={ocupado} className={botonSecundario} data-testid="alquiler-reabrir">
            Reabrir
          </button>
        ) : (
          <button type="button" onClick={() => validarOReabrir('validar')} disabled={ocupado || lineas.length === 0} className={botonPrincipal} data-testid="alquiler-validar">
            Validar alquiler
          </button>
        )}
      </div>

      {aviso && (
        <p
          className={`text-xs ${aviso.tipo === 'ok' ? 'text-[#0F5E43]' : aviso.tipo === 'info' ? 'text-[#7A3A06]' : 'text-[#8A1C12]'}`}
          data-testid="alquiler-mensaje"
        >
          {aviso.texto}
        </p>
      )}

      {/* Buscador del catálogo */}
      {!bloqueado && (
        <div className="rounded-[12px] border border-[#E1E6E3] p-3 flex flex-col gap-2" data-testid="alquiler-buscador">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar en el catálogo de alquiler (p. ej. plato llano 28)"
              className={`${claseInput} flex-1 min-w-0`}
              aria-label="Buscar en el catálogo de alquiler"
              data-testid="alquiler-buscar"
            />
            <select
              value={seccion}
              onChange={(e) => setSeccion(e.target.value)}
              className={`${claseInput} sm:w-52`}
              aria-label="Sección del catálogo"
              data-testid="alquiler-seccion"
            >
              <option value="">Todas las secciones</option>
              {SECCIONES_ALQUILER.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {catalogosFotos.length > 0 && (
              <button
                type="button"
                onClick={() => setCatalogoAbierto(true)}
                className={botonSecundario}
                data-testid="alquiler-ver-catalogo"
              >
                Ver catálogo con fotos
              </button>
            )}
          </div>
          {!busqueda.trim() && !seccion && (
            <p className="text-xs text-[#5B6B64]">Escribe para buscar o elige una sección.</p>
          )}
          {buscando && <p className="text-xs text-[#5B6B64]">Buscando...</p>}
          {errorBusqueda && <p className="text-xs text-[#8A1C12]">{errorBusqueda}</p>}
          {!buscando && (busqueda.trim() || seccion) && !errorBusqueda && resultados.length === 0 && (
            <p className="text-xs text-[#5B6B64]">No hay artículos con esa búsqueda.</p>
          )}
          {visibles.length > 0 && (
            <ul className="max-h-72 overflow-y-auto divide-y divide-[#EEF1EF] border border-[#EEF1EF] rounded-lg" data-testid="alquiler-resultados">
              {visibles.map((m) => {
                const yaEsta = idsEnLista.has(m.id);
                const infoUso = textoUsoAlquiler(usoAlquiler, m.id);
                return (
                  <li key={m.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#16221D] truncate">
                        {m.nombre}{m.medida ? <span className="font-normal text-[#5B6B64]"> · {m.medida}</span> : null}
                      </p>
                      <p className="text-xs text-[#5B6B64]">
                        {[
                          m.seccion_catalogo,
                          `${precio(m.precio_alquiler_dia1)} €/día`,
                          m.lavado_aparte || Number(m.coste_limpieza) > 0 ? `lavado ${precio(m.coste_limpieza)}` : null,
                        ].filter(Boolean).join(' · ')}
                      </p>
                      {infoUso && (
                        <p className={`text-xs ${infoUso.texto === 'Sin alquilar' ? 'text-[#8A958F]' : 'text-[#0F5E43]'}`} title={infoUso.titulo}>
                          {infoUso.texto}
                        </p>
                      )}
                    </div>
                    {yaEsta ? (
                      <span className="text-xs font-semibold text-[#0F5E43] whitespace-nowrap">En la lista</span>
                    ) : (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={cantidadesNuevas[m.id] ?? '1'}
                          onChange={(e) => setCantidadesNuevas(prev => ({ ...prev, [m.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') anadir(m); }}
                          className={`${claseInput} w-16 text-right tabular-nums`}
                          aria-label={`Cantidad de ${m.nombre}`}
                        />
                        <button type="button" onClick={() => anadir(m)} className={botonSecundario} data-testid={`alquiler-anadir-${m.id}`}>
                          Añadir
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {resultados.length > MAX_RESULTADOS && (
            <p className="text-xs text-[#7A3A06]">
              Se muestran {MAX_RESULTADOS} de {resultados.length} artículos: afina la búsqueda.
            </p>
          )}
        </div>
      )}

      {/* Días de alquiler de toda la orden */}
      {lineas.length > 0 && (
        <div className="rounded-[12px] border border-[#E1E6E3] px-3 py-2.5 flex flex-col gap-1.5" data-testid="alquiler-dias">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-[#16221D]">Días de alquiler</span>
            <div className="flex gap-1.5" role="group" aria-label="Días de alquiler">
              {[1, 2, 3].map((n) => {
                const activo = diasAlquiler === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => { if (!activo) cambiarDiasAlquiler(n); }}
                    disabled={bloqueado || ocupado}
                    aria-pressed={activo}
                    className={`h-11 md:h-[30px] min-w-[44px] md:min-w-[36px] px-2 rounded-lg border text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed ${activo ? 'border-duetto-primary bg-[#E3F3EC] text-[#0F5E43]' : 'border-[#E1E6E3] bg-white text-[#16221D] hover:border-[#9FC7B6]'}`}
                    data-testid={`alquiler-dias-${n}`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            <input
              type="number"
              min="1"
              max={DIAS_ALQUILER_MAX}
              step="1"
              value={diasTexto}
              onChange={(e) => setDiasTexto(e.target.value)}
              onBlur={() => { if (diasTexto !== String(datos.dias_alquiler || '')) cambiarDiasAlquiler(diasTexto); }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              disabled={bloqueado}
              className={`${claseInput} w-20 text-right tabular-nums`}
              aria-label="Número de días de alquiler"
              data-testid="alquiler-dias-numero"
            />
            {Number(datos.dias_evento) > 0 && (
              <span className="text-xs text-[#5B6B64]">
                El evento dura {Number(datos.dias_evento) === 1 ? '1 día' : `${datos.dias_evento} días`}.
              </span>
            )}
          </div>
          <p className="text-xs text-[#5B6B64]">Alea cobra por días de uso, no por los días que tienes el material.</p>
          {avisoFechas && (
            <p className="text-xs font-semibold text-[#7A3A06]" data-testid="alquiler-aviso-fechas">{avisoFechas}.</p>
          )}
        </div>
      )}

      {/* Líneas */}
      {lineas.length === 0 ? (
        <p className="text-sm text-[#5B6B64] border-2 border-dashed border-[#E1E6E3] rounded-lg py-5 text-center" data-testid="alquiler-vacio">
          No hay material de alquiler en este evento. Búscalo en el catálogo y añádelo.
        </p>
      ) : esMovil ? (
        // Móvil: una tarjeta por artículo (nombre + coste; datos en gris; cantidad,
        // cobro y Quitar con objetivos de 44 px). Mismos handlers que la tabla
        <ul className="flex flex-col gap-2" data-testid="alquiler-tabla">
          {lineas.map((l) => (
            <li key={l.material_id} className="rounded-lg border border-[#E1E6E3] bg-white p-3 flex flex-col gap-2"
              data-testid={`alquiler-linea-${l.material_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-[#16221D] break-words">{l.nombre}</div>
                  <div className="text-xs text-[#5B6B64]">
                    {[l.seccion, l.medida, l.referencia ? `ref. ${l.referencia}` : null, l.proveedor_nombre].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold tabular-nums text-[#16221D]">{euros(l.subtotal)}</div>
                  <div className="text-xs text-[#5B6B64]">coste</div>
                </div>
              </div>
              <p className="text-[13px] text-[#5B6B64] tabular-nums" data-testid={`alquiler-dias-linea-${l.material_id}`}>
                {Number(l.dias) || diasAlquiler} {(Number(l.dias) || diasAlquiler) === 1 ? 'día' : 'días'} · {precio(l.precio_dia1)} €/día
                {Number(l.dias) > 1 && Number(l.precio_dia_extra) > 0 ? ` (+${precio(l.precio_dia_extra)} €/día extra)` : ''}
                {Number(l.coste_limpieza) > 0 ? ` · limpieza ${precio(l.coste_limpieza)} €` : ''}
              </p>
              <label className="flex items-center justify-between gap-3 text-[13px] text-[#33443C]">
                Cantidad
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={valorLinea(l, 'cantidad')}
                  onChange={(e) => cambiarBorrador(l.material_id, 'cantidad', e.target.value)}
                  onBlur={guardarLineasYa}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  disabled={bloqueado}
                  className={`${claseInput} w-28 h-11 text-right tabular-nums`}
                  aria-label={`Cantidad de ${l.nombre}`}
                  data-testid={`alquiler-cantidad-${l.material_id}`}
                />
              </label>
              <div className="border-t border-[#EEF1EF] pt-2">
                <p className="text-xs font-semibold text-[#5B6B64] mb-1">Cobro al cliente (el margen va sobre el alquiler; limpieza y porte, a coste)</p>
                <CobroLinea linea={l} ocupado={ocupado} onCambiar={(campos) => cambiarCobro(l.material_id, campos)} />
              </div>
              {!bloqueado && (
                <button
                  type="button"
                  onClick={() => quitarLinea(l)}
                  className="w-full h-11 rounded-lg border border-red-200 bg-white text-sm font-semibold text-[#8A1C12] active:bg-red-50"
                  data-testid={`alquiler-quitar-${l.material_id}`}
                >
                  Quitar
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse" data-testid="alquiler-tabla">
            <thead>
              <tr>
                <th className={`${claseTh} text-left`}>Artículo</th>
                <th className={`${claseTh} text-left`}>Sección</th>
                <th className={`${claseTh} text-right`}>Cantidad</th>
                <th className={`${claseTh} text-right`}>Días</th>
                <th className={`${claseTh} text-right`}>Precio/día</th>
                <th className={`${claseTh} text-right`}>Limpieza</th>
                <th className={`${claseTh} text-right`}>Coste</th>
                <th className={`${claseTh} text-left`}>
                  Cobro al cliente
                  <span className="block normal-case font-normal tracking-normal text-xs md:text-[11px]">El margen va sobre el alquiler. Limpieza y porte, a coste.</span>
                </th>
                <th className={claseTh}><span className="sr-only">Quitar</span></th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((l) => (
                <tr key={l.material_id} data-testid={`alquiler-linea-${l.material_id}`}>
                  <td className={claseTd}>
                    <div className="font-semibold text-[#16221D]">{l.nombre}</div>
                    <div className="text-xs text-[#5B6B64]">
                      {[l.medida, l.referencia ? `ref. ${l.referencia}` : null, l.proveedor_nombre].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td className={`${claseTd} text-xs text-[#5B6B64]`}>{l.seccion || '-'}</td>
                  <td className={`${claseTd} text-right`}>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={valorLinea(l, 'cantidad')}
                      onChange={(e) => cambiarBorrador(l.material_id, 'cantidad', e.target.value)}
                      onBlur={guardarLineasYa}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      disabled={bloqueado}
                      className={`${claseInput} w-20 text-right tabular-nums`}
                      aria-label={`Cantidad de ${l.nombre}`}
                      data-testid={`alquiler-cantidad-${l.material_id}`}
                    />
                  </td>
                  <td className={`${claseTd} text-right tabular-nums`} data-testid={`alquiler-dias-linea-${l.material_id}`}>
                    {Number(l.dias) || diasAlquiler}
                  </td>
                  <td className={`${claseTd} text-right tabular-nums text-xs`}>
                    {precio(l.precio_dia1)} €
                    {Number(l.dias) > 1 && Number(l.precio_dia_extra) > 0 && (
                      <div className="text-[#5B6B64]">+{precio(l.precio_dia_extra)} €/día extra</div>
                    )}
                  </td>
                  <td className={`${claseTd} text-right tabular-nums text-xs`}>
                    {Number(l.coste_limpieza) > 0 ? `${precio(l.coste_limpieza)} €` : '-'}
                  </td>
                  <td className={`${claseTd} text-right tabular-nums font-semibold`}>{euros(l.subtotal)}</td>
                  <td className={claseTd}>
                    <CobroLinea linea={l} ocupado={ocupado} onCambiar={(campos) => cambiarCobro(l.material_id, campos)} />
                  </td>
                  <td className={`${claseTd} text-right`}>
                    {!bloqueado && (
                      <button
                        type="button"
                        onClick={() => quitarLinea(l)}
                        className="text-xs font-semibold text-[#8A1C12] hover:underline"
                        data-testid={`alquiler-quitar-${l.material_id}`}
                      >
                        Quitar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Transporte, fianza y observaciones */}
      <div className="flex flex-col gap-3">
        <div>
          <span className={claseEtiqueta}>Transporte del material</span>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Modalidad de transporte">
            {MODALIDADES.map((m) => {
              const activa = modalidad === m.valor;
              return (
                <button
                  key={m.valor}
                  type="button"
                  onClick={() => { if (!activa) guardar({ modalidad_transporte: m.valor }); }}
                  disabled={bloqueado}
                  aria-pressed={activa}
                  className={`px-3 py-1.5 min-h-[44px] md:min-h-0 text-left md:text-center rounded-lg border text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed ${activa ? 'border-duetto-primary bg-[#E3F3EC] text-[#0F5E43]' : 'border-[#E1E6E3] bg-white text-[#16221D] hover:border-[#9FC7B6]'}`}
                  data-testid={`alquiler-modalidad-${m.valor}`}
                >
                  {m.texto}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={claseEtiqueta} htmlFor="alquiler-transporte">Transporte estimado (€)</label>
            <input
              id="alquiler-transporte"
              type="number"
              min="0"
              step="0.01"
              value={transporte}
              onChange={(e) => setTransporte(e.target.value)}
              onBlur={() => guardarImporte('alquiler_transporte', transporte, datos.alquiler_transporte)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              disabled={bloqueado}
              placeholder="0,00"
              className={`${claseInput} w-full tabular-nums`}
              data-testid="alquiler-transporte"
            />
            <p className="text-xs text-[#5B6B64] mt-1">Alea cobra de 80 a 95 € por servicio si lo lleva; 0 si se recoge.</p>
          </div>
          <div>
            <label className={claseEtiqueta} htmlFor="alquiler-fianza">Fianza (€)</label>
            <input
              id="alquiler-fianza"
              type="number"
              min="0"
              step="0.01"
              value={fianza}
              onChange={(e) => setFianza(e.target.value)}
              onBlur={() => guardarImporte('alquiler_fianza', fianza, datos.alquiler_fianza)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              disabled={bloqueado}
              placeholder="0,00"
              className={`${claseInput} w-full tabular-nums`}
              data-testid="alquiler-fianza"
            />
            <p className="text-xs text-[#5B6B64] mt-1">No es gasto: se devuelve al entregar el material.</p>
          </div>
        </div>
        <div>
          <label className={claseEtiqueta} htmlFor="alquiler-observaciones">Observaciones del alquiler</label>
          <textarea
            id="alquiler-observaciones"
            rows={2}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            onBlur={() => { if (observaciones !== (datos.observaciones_alquiler || '')) guardar({ observaciones_alquiler: observaciones }); }}
            disabled={bloqueado}
            className={`${claseInput} w-full`}
            data-testid="alquiler-observaciones"
          />
        </div>
      </div>

      {/* Totales: coste, cobro al cliente y margen (el cobro de cada artículo, en su línea) */}
      <div className="rounded-lg bg-[#F7F9F8] px-3.5 py-3 text-sm flex flex-col gap-1" data-testid="alquiler-totales">
        <div className="flex flex-wrap justify-between gap-x-6 gap-y-1">
          <span>
            <span className="text-[#5B6B64]">Coste </span>
            <span className="tabular-nums font-bold" data-testid="alquiler-total-coste">{euros(totales.coste_referencia ?? totales.coste_total)}</span>
          </span>
          <span>
            <span className="text-[#5B6B64]">Cobro al cliente </span>
            <span className="tabular-nums font-bold" data-testid="alquiler-total-cobro">{euros(totales.venta_total)}</span>
          </span>
          <span>
            <span className="text-[#5B6B64]">Margen </span>
            <span className="tabular-nums font-bold" data-testid="alquiler-total-margen">
              {totales.margen_total_pct === null || totales.margen_total_pct === undefined
                ? '-'
                : `${Number(totales.margen_total_pct) > 0 ? '+' : ''}${Number(totales.margen_total_pct).toLocaleString('es-ES', { maximumFractionDigits: 1 })} %`}
            </span>
          </span>
        </div>
        {Number(totales.porte_por_unidad) > 0 && (
          <p className="text-xs text-[#5B6B64]" data-testid="alquiler-porte-unidad">
            Porte repartido: {euros(totales.porte_por_unidad)} por unidad. Limpieza y porte se cobran a coste.
          </p>
        )}
      </div>

      {/* Orden de pedido al proveedor */}
      {lineas.length > 0 && (
        <div className="rounded-[12px] border border-[#E1E6E3] p-3.5 flex flex-col gap-3" data-testid="alquiler-orden">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-bold text-[#16221D]">Orden de pedido al proveedor</h4>
            {orden && (
              <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold ${infoEstado.clase}`} data-testid="alquiler-orden-estado">
                {infoEstado.texto}
              </span>
            )}
          </div>

          {cotRecibida && (
            <TarjetaCotizacionRecibida
              cotizacion={cotRecibida}
              proveedor={proveedorOrden?.nombre || 'Alea'}
              ocupado={ocupado}
              onAplicar={aplicarCotizacionRecibida}
              onDescartar={descartarCotizacionRecibida}
            />
          )}

          {!orden ? (
            <div className="flex flex-wrap items-center justify-between gap-2 bg-[#F7F9F8] rounded-lg px-3 py-3">
              <p className="text-xs text-[#5B6B64]">
                Todavía no hay orden. Se prepara con las líneas de arriba y los datos del proveedor{proveedor?.nombre ? ` (${proveedor.nombre})` : ''}.
              </p>
              <button type="button" onClick={prepararOrden} disabled={ocupado} className={botonPrincipal} data-testid="alquiler-preparar-orden">
                Preparar orden
              </button>
            </div>
          ) : (
            <>
              <div className="text-xs text-[#16221D] grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                <span><span className="text-[#5B6B64]">Proveedor:</span> {proveedorOrden?.nombre || 'sin proveedor'}</span>
                <span><span className="text-[#5B6B64]">Email:</span> {proveedorOrden?.email || 'sin email'}</span>
                <span><span className="text-[#5B6B64]">Teléfono:</span> {proveedorOrden?.telefono || '-'}</span>
                <span><span className="text-[#5B6B64]">Nº de cliente:</span> {proveedorOrden?.numero_cliente || '-'}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={claseEtiqueta} htmlFor="orden-alq-modalidad">Modalidad</label>
                  <select
                    id="orden-alq-modalidad"
                    value={formOrden.modalidad}
                    onChange={(e) => cambiarOrden('modalidad', e.target.value)}
                    className={`${claseInput} w-full`}
                  >
                    {MODALIDADES.map(m => <option key={m.valor} value={m.valor}>{m.texto}</option>)}
                  </select>
                </div>
                <div>
                  <span className={claseEtiqueta}>Entrega</span>
                  <div className="flex gap-2">
                    <input type="date" value={formOrden.fecha_entrega} onChange={(e) => cambiarOrden('fecha_entrega', e.target.value)} className={`${claseInput} flex-1 min-w-0`} aria-label="Fecha de entrega" data-testid="orden-alq-fecha-entrega" />
                    <input type="time" value={formOrden.hora_entrega} onChange={(e) => cambiarOrden('hora_entrega', e.target.value)} className={`${claseInput} w-28`} aria-label="Hora de entrega" />
                  </div>
                </div>
                <div>
                  <span className={claseEtiqueta}>Recogida</span>
                  <div className="flex gap-2">
                    <input type="date" value={formOrden.fecha_recogida} onChange={(e) => cambiarOrden('fecha_recogida', e.target.value)} className={`${claseInput} flex-1 min-w-0`} aria-label="Fecha de recogida" data-testid="orden-alq-fecha-recogida" />
                    <input type="time" value={formOrden.hora_recogida} onChange={(e) => cambiarOrden('hora_recogida', e.target.value)} className={`${claseInput} w-28`} aria-label="Hora de recogida" />
                  </div>
                </div>
                <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#F7F9F8] px-3 py-2" data-testid="alquiler-orden-fechas">
                  <div className="text-xs text-[#16221D] flex flex-col gap-0.5">
                    <span>Días de alquiler (uso): <strong>{diasAlquiler}</strong></span>
                    {avisoFechas ? (
                      <span className="font-semibold text-[#7A3A06]">{avisoFechas}.</span>
                    ) : orden.fechas_confirmadas ? (
                      <span className="text-[#0F5E43]" data-testid="alquiler-fechas-confirmadas">
                        Fechas confirmadas{orden.fechas_confirmadas_por ? ` por ${orden.fechas_confirmadas_por}` : ''}
                      </span>
                    ) : (
                      <span className="text-[#5B6B64]">Revisa las fechas propuestas antes de enviar la orden.</span>
                    )}
                  </div>
                  {!orden.fechas_confirmadas && (
                    <button
                      type="button"
                      onClick={confirmarFechas}
                      disabled={ocupado || !formOrden.fecha_entrega || !formOrden.fecha_recogida}
                      className={avisoFechas ? botonPrincipal : botonSecundario}
                      data-testid="alquiler-confirmar-fechas"
                    >
                      Confirmar fechas
                    </button>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className={claseEtiqueta} htmlFor="orden-alq-direccion">Dirección de entrega</label>
                  <input id="orden-alq-direccion" type="text" value={formOrden.direccion_entrega} onChange={(e) => cambiarOrden('direccion_entrega', e.target.value)} className={`${claseInput} w-full`} />
                </div>
                <div>
                  <label className={claseEtiqueta} htmlFor="orden-alq-contacto">Contacto en el sitio</label>
                  <input id="orden-alq-contacto" type="text" value={formOrden.contacto_nombre} onChange={(e) => cambiarOrden('contacto_nombre', e.target.value)} placeholder="Nombre" className={`${claseInput} w-full`} />
                </div>
                <div>
                  <label className={claseEtiqueta} htmlFor="orden-alq-telefono">Teléfono del contacto</label>
                  <input id="orden-alq-telefono" type="tel" value={formOrden.contacto_telefono} onChange={(e) => cambiarOrden('contacto_telefono', e.target.value)} className={`${claseInput} w-full`} />
                </div>
                <div className="sm:col-span-2">
                  <label className={claseEtiqueta} htmlFor="orden-alq-observaciones">Observaciones para el proveedor</label>
                  <textarea id="orden-alq-observaciones" rows={2} value={formOrden.observaciones} onChange={(e) => cambiarOrden('observaciones', e.target.value)} className={`${claseInput} w-full`} />
                </div>
              </div>

              {esMovil ? (
                <div>
                  <ul className="flex flex-col divide-y divide-[#EEF1EF] border border-[#E1E6E3] rounded-lg" data-testid="alquiler-orden-lineas">
                    {(orden.lineas || []).map(l => (
                      <li key={l.material_id} className="px-3 py-2 text-sm">
                        <div className="flex items-start justify-between gap-3">
                          <span className="min-w-0 break-words text-[#16221D]">{l.nombre}</span>
                          <span className="tabular-nums font-semibold whitespace-nowrap">{l.cantidad} uds</span>
                        </div>
                        <div className="text-xs text-[#5B6B64] tabular-nums">
                          {l.referencia ? `ref. ${l.referencia} · ` : ''}{l.dias} {Number(l.dias) === 1 ? 'día' : 'días'} · {precio(l.precio_dia1)} €/día
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-[#5B6B64] mt-1">Las líneas salen de la lista de arriba.</p>
                </div>
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px] border-collapse" data-testid="alquiler-orden-lineas">
                  <thead>
                    <tr>
                      <th className={`${claseTh} text-left`}>Ref.</th>
                      <th className={`${claseTh} text-left`}>Artículo</th>
                      <th className={`${claseTh} text-right`}>Cantidad</th>
                      <th className={`${claseTh} text-right`}>Días</th>
                      <th className={`${claseTh} text-right`}>Precio/día</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(orden.lineas || []).map(l => (
                      <tr key={l.material_id}>
                        <td className={`${claseTd} text-xs text-[#5B6B64]`}>{l.referencia || '-'}</td>
                        <td className={claseTd}>{l.nombre}</td>
                        <td className={`${claseTd} text-right tabular-nums`}>{l.cantidad}</td>
                        <td className={`${claseTd} text-right tabular-nums`}>{l.dias}</td>
                        <td className={`${claseTd} text-right tabular-nums`}>{precio(l.precio_dia1)} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-[#5B6B64] mt-1">Las líneas salen de la tabla de arriba.</p>
              </div>
              )}

              {costeReal && (
                <div className="rounded-lg bg-[#EEF4FB] px-3 py-2 text-xs text-[#1E3A5F]" data-testid="alquiler-coste-real">
                  Cotización confirmada{costeReal.numero_presupuesto_proveedor ? ` (presupuesto ${costeReal.numero_presupuesto_proveedor})` : ''}:
                  {' '}material {euros(costeReal.material)}, transporte {euros(costeReal.transporte)}, lavado {euros(costeReal.lavado)}
                  {Number(costeReal.faltas) > 0 ? `, faltas ${euros(costeReal.faltas)}` : ''}. Total <strong>{euros(costeReal.total)}</strong>
                  {costeReal.fecha ? ` · ${formatFecha(costeReal.fecha)}` : ''}
                </div>
              )}
              {(Number(orden.fianza_importe) > 0 || orden.fianza_pagada_en || orden.fianza_devuelta_en) && (
                <p className="text-xs text-[#5B6B64]">
                  Fianza {euros(orden.fianza_importe)}
                  {orden.fianza_pagada_en ? ` · pagado el ${formatFecha(orden.fianza_pagada_en)}` : ' · sin pagar'}
                  {orden.fianza_devuelta_en ? ` · devuelta el ${formatFecha(orden.fianza_devuelta_en)}` : ''}
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={guardarOrden} disabled={ocupado || !ordenModificada} className={botonPrincipal} data-testid="alquiler-orden-guardar">
                  Guardar
                </button>
                <button type="button" onClick={descargarPdf} disabled={ocupado} className={botonSecundario} data-testid="alquiler-orden-pdf">
                  Descargar PDF
                </button>
                {estadoOrden !== 'devuelta' && (
                  <button
                    type="button"
                    onClick={abrirEnvio}
                    disabled={ocupado || !!avisoFechas}
                    title={avisoFechas || undefined}
                    className={estadoOrden === 'borrador' ? botonPrincipal : botonSecundario}
                    data-testid="alquiler-orden-enviar"
                  >
                    {estadoOrden === 'borrador' ? 'Enviar al proveedor' : 'Reenviar al proveedor'}
                  </button>
                )}
                {['enviada', 'confirmada'].includes(estadoOrden) && (
                  <button type="button" onClick={abrirConfirmar} disabled={ocupado} className={estadoOrden === 'enviada' ? botonPrincipal : botonSecundario} data-testid="alquiler-orden-confirmar">
                    {estadoOrden === 'enviada' ? 'Confirmar con la cotización' : 'Corregir la cotización'}
                  </button>
                )}
                {['confirmada', 'recogida', 'devuelta'].includes(estadoOrden) && !orden.fianza_pagada_en && (
                  <button type="button" onClick={marcarPagado} disabled={ocupado} className={botonSecundario} data-testid="alquiler-orden-pagado">
                    Marcar pagado
                  </button>
                )}
                {estadoOrden === 'confirmada' && (
                  <button type="button" onClick={marcarRecogida} disabled={ocupado} className={botonSecundario} data-testid="alquiler-orden-recogida">
                    Marcar recogida
                  </button>
                )}
                {['confirmada', 'recogida'].includes(estadoOrden) && (
                  <button type="button" onClick={() => setModalDevuelta({ fecha: hoyISO() })} disabled={ocupado} className={botonSecundario} data-testid="alquiler-orden-devuelta">
                    Marcar devuelta
                  </button>
                )}
              </div>
              {ordenModificada && <p className="text-xs text-[#7A3A06]">Hay cambios sin guardar en la orden.</p>}

              {envios.length > 0 && (
                <div className="text-xs text-[#5B6B64]" data-testid="alquiler-orden-envios">
                  <p className="font-semibold text-[#16221D] mb-0.5">Envíos</p>
                  <ul className="flex flex-col gap-0.5">
                    {envios.map(e => (
                      <li key={`${e.fecha}-${e.para}`}>
                        {formatFechaHora(e.fecha)} · a {e.para || 'sin destinatario'}{e.por ? ` · por ${e.por}` : ''}{e.asunto ? ` · ${e.asunto}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Recepción en el evento (evento no celebrado o celebrado hoy) */}
      {datos.hay_recepcion && datos.recepcion_aplica && (
        <RecepcionAlquiler recepcion={datos.recepcion} ocupado={ocupado} onGuardar={guardarRecepcion} />
      )}

      {/* Visor del catálogo con fotos (no se cierra al añadir) */}
      {catalogosFotos.length > 0 && (
        <CatalogoFotosAlquiler
          abierto={catalogoAbierto}
          onCerrar={() => setCatalogoAbierto(false)}
          catalogos={catalogosFotos}
          cantidadesEnLista={cantidadesEnLista}
          onAnadir={anadirLinea}
          uso={usoAlquiler}
          aviso={aviso}
          bloqueado={bloqueado}
        />
      )}

      {/* Modal: enviar la orden */}
      <Dialog open={!!modalEnvio} onOpenChange={(abierto) => { if (!abierto && !ocupado) setModalEnvio(null); }}>
        <DialogContent className="bg-white w-[calc(100vw-1rem)] max-w-lg max-h-[92vh] overflow-y-auto p-4 md:p-6 md:max-h-none md:overflow-visible">
          <DialogHeader>
            <DialogTitle>Enviar la orden al proveedor</DialogTitle>
            <DialogDescription>Se envía por email con el PDF de la orden adjunto.</DialogDescription>
          </DialogHeader>
          {modalEnvio && (
            <div className="flex flex-col gap-2.5">
              <div>
                <label className={claseEtiqueta} htmlFor="envio-alq-para">Para</label>
                <input id="envio-alq-para" type="email" value={modalEnvio.para} onChange={(e) => setModalEnvio(p => ({ ...p, para: e.target.value }))} className={`${claseInput} w-full`} />
              </div>
              <div>
                <label className={claseEtiqueta} htmlFor="envio-alq-cc">CC</label>
                <input id="envio-alq-cc" type="text" value={modalEnvio.cc} onChange={(e) => setModalEnvio(p => ({ ...p, cc: e.target.value }))} placeholder="Opcional, separados por comas" className={`${claseInput} w-full`} />
              </div>
              <div>
                <label className={claseEtiqueta} htmlFor="envio-alq-asunto">Asunto</label>
                <input id="envio-alq-asunto" type="text" value={modalEnvio.asunto} onChange={(e) => setModalEnvio(p => ({ ...p, asunto: e.target.value }))} className={`${claseInput} w-full`} />
              </div>
              <div>
                <label className={claseEtiqueta} htmlFor="envio-alq-cuerpo">Mensaje</label>
                <textarea id="envio-alq-cuerpo" rows={8} value={modalEnvio.cuerpo} onChange={(e) => setModalEnvio(p => ({ ...p, cuerpo: e.target.value }))} className={`${claseInput} w-full`} />
              </div>
              {ordenModificada && <p className="text-xs text-[#7A3A06]">Los cambios sin guardar de la orden se guardan antes de enviarla.</p>}
            </div>
          )}
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setModalEnvio(null)} disabled={ocupado} className={botonSecundario}>Cancelar</button>
            <button type="button" onClick={enviarOrden} disabled={ocupado} className={botonPrincipal} data-testid="alquiler-envio-confirmar">
              {ocupado ? 'Enviando...' : `Enviar a ${modalEnvio?.para || 'proveedor'}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: confirmar con la cotización del proveedor */}
      <Dialog open={!!modalConfirmar} onOpenChange={(abierto) => { if (!abierto && !ocupado) setModalConfirmar(null); }}>
        <DialogContent className="bg-white w-[calc(100vw-1rem)] max-w-md max-h-[92vh] overflow-y-auto p-4 md:p-6 md:max-h-none md:overflow-visible">
          <DialogHeader>
            <DialogTitle>Confirmar con la cotización</DialogTitle>
            <DialogDescription>Importes sin IVA del presupuesto del proveedor. Pasan a ser el coste real del alquiler.</DialogDescription>
          </DialogHeader>
          {modalConfirmar && (
            <div className="flex flex-col gap-2.5">
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  ['material', 'Material (€)'],
                  ['transporte', 'Transporte (€)'],
                  ['lavado', 'Lavado (€)'],
                  ['faltas', 'Faltas o roturas (€)'],
                  ['fianza', 'Fianza (€)'],
                ].map(([campo, etiqueta]) => (
                  <div key={campo}>
                    <label className={claseEtiqueta} htmlFor={`confirmar-alq-${campo}`}>{etiqueta}</label>
                    <input
                      id={`confirmar-alq-${campo}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={modalConfirmar[campo]}
                      onChange={(e) => setModalConfirmar(p => ({ ...p, [campo]: e.target.value }))}
                      className={`${claseInput} w-full tabular-nums`}
                    />
                  </div>
                ))}
                <div>
                  <label className={claseEtiqueta} htmlFor="confirmar-alq-numero">Nº de presupuesto del proveedor</label>
                  <input id="confirmar-alq-numero" type="text" value={modalConfirmar.numero} onChange={(e) => setModalConfirmar(p => ({ ...p, numero: e.target.value }))} className={`${claseInput} w-full`} />
                </div>
              </div>
              <div className="rounded-lg bg-[#F7F9F8] px-3 py-2 text-sm flex flex-col gap-0.5">
                <div className="flex justify-between font-bold"><span>Total cotización</span><span className="tabular-nums">{euros(totalConfirmar)}</span></div>
                <div className="flex justify-between text-xs text-[#5B6B64]"><span>Estimación de la ficha</span><span className="tabular-nums">{euros(estimacion)}</span></div>
                {estimacion > 0 && (
                  <div className={`flex justify-between text-xs ${totalConfirmar > estimacion ? 'text-[#8A1C12]' : 'text-[#0F5E43]'}`}>
                    <span>Diferencia</span>
                    <span className="tabular-nums">
                      {totalConfirmar - estimacion >= 0 ? '+' : ''}{euros(totalConfirmar - estimacion)} ({(((totalConfirmar - estimacion) / estimacion) * 100).toLocaleString('es-ES', { maximumFractionDigits: 1 })} %)
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setModalConfirmar(null)} disabled={ocupado} className={botonSecundario}>Cancelar</button>
            <button type="button" onClick={confirmarCotizacion} disabled={ocupado} className={botonPrincipal} data-testid="alquiler-confirmar-guardar">
              Confirmar cotización
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: material devuelto */}
      <Dialog open={!!modalDevuelta} onOpenChange={(abierto) => { if (!abierto && !ocupado) setModalDevuelta(null); }}>
        <DialogContent className="bg-white w-[calc(100vw-1rem)] max-w-sm p-4 md:p-6">
          <DialogHeader>
            <DialogTitle>Material devuelto</DialogTitle>
            <DialogDescription>Marca la orden como devuelta y anota cuándo se devolvió la fianza.</DialogDescription>
          </DialogHeader>
          {modalDevuelta && (
            <div>
              <label className={claseEtiqueta} htmlFor="devuelta-alq-fecha">Fecha de devolución de la fianza</label>
              <input
                id="devuelta-alq-fecha"
                type="date"
                value={modalDevuelta.fecha}
                onChange={(e) => setModalDevuelta({ fecha: e.target.value })}
                className={`${claseInput} w-full`}
              />
              <p className="text-xs text-[#5B6B64] mt-1">Déjala vacía si la fianza aún no se ha devuelto.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setModalDevuelta(null)} disabled={ocupado} className={botonSecundario}>Cancelar</button>
            <button type="button" onClick={marcarDevuelta} disabled={ocupado} className={botonPrincipal} data-testid="alquiler-devuelta-guardar">
              Marcar devuelta
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlquilerEvento;
